'use client'

import { getAccessToken, getIdentityToken } from '@privy-io/react-auth'

/**
 * getIdentityToken() is NOT cached by the SDK — every call hits
 * GET auth.privy.io/api/v1/users/me. With our polling hooks that
 * rate-limits the app (429) and drops the privy-id-token header,
 * which cascades into 401s server-side. The SDK keeps the current
 * identity token in localStorage (privy:id_token), so read that and
 * only refresh over the network when it's missing or near expiry,
 * deduping concurrent refreshes.
 */
const ID_TOKEN_STORAGE_KEY = 'privy:id_token'
const EXPIRY_MARGIN_MS = 60_000

let refreshPromise: Promise<string | null> | null = null

function jwtExpiryMs(token: string): number {
	try {
		const payload = JSON.parse(atob(token.split('.')[1] ?? ''))
		return typeof payload.exp === 'number' ? payload.exp * 1000 : 0
	} catch {
		return 0
	}
}

function readStoredIdToken(): string | null {
	try {
		const raw = window.localStorage.getItem(ID_TOKEN_STORAGE_KEY)
		if (!raw) return null
		// Stored either as a bare string or JSON-quoted string
		return raw.startsWith('"') ? (JSON.parse(raw) as string) : raw
	} catch {
		return null
	}
}

async function getCachedIdentityToken(): Promise<string | null> {
	const stored = readStoredIdToken()
	if (stored && jwtExpiryMs(stored) - Date.now() > EXPIRY_MARGIN_MS) return stored

	refreshPromise ??= getIdentityToken()
		.catch(() => stored)
		.finally(() => {
			refreshPromise = null
		})
	return refreshPromise
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}) {
	const [token, idToken] = await Promise.all([getAccessToken(), getCachedIdentityToken()])

	const headers = new Headers(init.headers)
	if (token) headers.set('authorization', `Bearer ${token}`)
	if (idToken) headers.set('privy-id-token', idToken)

	return fetch(input, { ...init, headers })
}
